import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from "@material-ui/core";
import { getLocalizedString } from "@icarius-localization/strings";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import { createLocationSettingsAction, updateLocationSettingsAction } from "../../actions";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import useHandleForm from "./useHandleForm";
import MapWithASearchBox from "./map";
import PlayButton from "@icarius-common/audioPlayer/components/playButton";
import CustomNumberFormat from '@icarius-common/dynamicInputs/customNumberFormat';

const gridStyle = { height: 75, padding: "0px 10px" };

const Form = (props) => {

  const {
    data,
    types,
    establishmentTypes,
    riskCenters,
    holidayTypes,
    handleClose,
    country,
  } = props;

  const dispatch = useDispatch();

  const [mapIsOpen, setMapIsOpen] = useState(false);

  const create = (dataToSend) => {
    dispatch(createLocationSettingsAction(dataToSend))
      .then((res) => res?.status === 200 && handleClose());
  }

  const modify = (dataToSend) => {
    dispatch(updateLocationSettingsAction(dataToSend))
      .then((res) => res?.status === 200 && handleClose());
  }

  const openValidationError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
  }

  const {
    isCreate,
    formData,
    setFormValue,
    submit
  } = useHandleForm(data, create, modify, openValidationError);

  const handleSetMapData = (data) => {
    setFormValue(data.name, "address");
    setFormValue(`${data.lat},${data.long}`, "coordinates");
  }

  return (
    <Grid container item xs={12} direction="row" style={{ paddingTop: 10 }}>
      <Grid container item xs={12}>
        <Grid container item alignItems="center" xs={12} style={{ justifyContent: "center", gap: 10 }}>
          <Button variant="outlined" onClick={() => setMapIsOpen(prev => !prev)} style={{ minWidth: 200 }}>
            {mapIsOpen ? getLocalizedString("closeMap") : getLocalizedString("openMap")}
          </Button>
          <PlayButton audioName="Abrir mapa (Latitud y Longitud)" title={getLocalizedString("openMapLatitudeLongitude")} />
        </Grid>
        <Grid container item alignItems="center" xs={12} sm={6} md={2} style={gridStyle}> {/* codigo */}
          <TextField
            disabled={!Boolean(isCreate)}
            required
            fullWidth
            margin={"none"}
            label={getLocalizedString("centerCode")}
            onChange={(e) => setFormValue(e.target.value, "code")}
            value={formData.code}
            inputProps={{ maxLength: 8 }}
          />
        </Grid>
        <Grid container item alignItems="center" xs={12} sm={6} md={5} style={gridStyle}> {/* nombre */}
          <TextField
            required
            fullWidth
            margin={"none"}
            label={getLocalizedString("workplaceName")}
            value={formData.name}
            onChange={(e) => setFormValue(e.target.value, "name")}
            inputProps={{ maxLength: 60 }}
          />
        </Grid>
        <Grid container item alignItems="center" xs={12} sm={6} md={5} style={gridStyle}> {/* dirección */}
          <TextField
            required={formData.code !== "-"}
            fullWidth
            margin={"none"}
            label={getLocalizedString("address")}
            value={formData.address}
            onChange={(e) => setFormValue(e.target.value, "address")}
            inputProps={{ maxLength: 100 }}
          />
        </Grid>
        {
          country === "CL" &&
          <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}> {/* zip code */}
            <TextField
              fullWidth
              margin={"none"}
              label={getLocalizedString("postalCode")}
              value={formData.zipCode}
              onChange={(e) => setFormValue(e.target.value, "zipCode")}
              inputProps={{ maxLength: 60 }}
            />
          </Grid>
        }
        <Grid container item alignItems="center" xs={12} sm={6} md={6} style={gridStyle}> {/* coordenadas */}
          <TextField
            fullWidth
            margin={"none"}
            label={getLocalizedString("latitudeAndLongitude")}
            value={formData.coordinates}
            onChange={(e) => setFormValue(e.target.value, "coordinates")}
            inputProps={{ maxLength: 41 }}
          />
        </Grid>
        <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}> {/* range */}
          <div style={{ display: "flex", alignItems: 'center', width: "100%", gap: 10 }}>
            <TextField
              required={Boolean(formData.coordinates)}
              fullWidth
              margin={"none"}
              label={getLocalizedString("attendanceMarkingRange")}
              value={formData.range}
              onChange={(e) => setFormValue(e.target.value, "range")}
            />
            <PlayButton audioName="Rango permitido desde la geoposición" title={getLocalizedString("allowedRangeFromGeoposition")} />
          </div>
        </Grid>
        <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}> {/* tipo */}
          <FormControl style={{ width: "100%" }}>
            <InputLabel id={`label-type-${data ? data.internalCode : "new"}`}>{getLocalizedString("type")}</InputLabel>
            <Select
              value={formData.type}
              labelId={`label-type-${data ? data.internalCode : "new"}`}
              id={`select-type-${data ? data.internalCode : "new"}`}
              onChange={(e) => setFormValue(e.target.value, "type")}
              margin={"none"}
            >
              {
                types && types.map(item => (
                  <MenuItem
                    className={"whiteText"}
                    key={item.key}
                    value={item.key}>
                    {item.value}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </Grid>
        {
          country === "CL" &&
          <>
            <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}> {/* commune */}
              <TextField
                fullWidth
                margin={"none"}
                label={getLocalizedString("comuna")}
                value={formData.commune}
                onChange={(e) => setFormValue(e.target.value, "commune")}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}> {/* realEstateRole */}
              <TextField
                fullWidth
                margin={"none"}
                label={getLocalizedString("realEstateAppraisalRole")}
                value={formData.realEstateRole}
                onChange={(e) => setFormValue(e.target.value, "realEstateRole")}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}> {/* patentRole */}
              <TextField
                fullWidth
                margin={"none"}
                label={getLocalizedString("municipalPatentRole")}
                value={formData.patentRole}
                onChange={(e) => setFormValue(e.target.value, "patentRole")}
                inputProps={{ maxLength: 30 }}
              />
            </Grid>
          </>
        }
        {
          country === "PE" &&
          <>

            <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}> {/* establishmentType */}
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-establishmentType-${data ? data.internalCode : "new"}`}>{getLocalizedString("establishmentType")}</InputLabel>
                <Select
                  value={formData.establishmentType}
                  labelId={`label-establishmentType-${data ? data.internalCode : "new"}`}
                  id={`select-establishmentType-${data ? data.internalCode : "new"}`}
                  onChange={(e) => setFormValue(e.target.value, "establishmentType")}
                  margin={"none"}
                >
                  {
                    establishmentTypes && establishmentTypes.map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}> {/* establishmentCode */}
              <TextField
                fullWidth
                margin={"none"}
                label={getLocalizedString("establishmentCode")}
                value={formData.establishmentCode}
                onChange={(e) => setFormValue(e.target.value, "establishmentCode")}
                inputProps={{ maxLength: 4 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}> {/* rate */}
              <TextField
                fullWidth
                margin={"none"}
                label={getLocalizedString("rate")}
                value={formData.rate}
                onChange={(e) => setFormValue(e.target.value, "rate")}
                InputProps={{ inputComponent: CustomNumberFormat }}
                inputProps={{ min: "0", step: "0.01", decimals: 2 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}> {/* riskCenter */}
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-riskCenter-${data ? data.internalCode : "new"}`}>{getLocalizedString("riskCenter")}</InputLabel>
                <Select
                  value={formData.riskCenter}
                  labelId={`label-riskCenter-${data ? data.internalCode : "new"}`}
                  id={`select-riskCenter-${data ? data.internalCode : "new"}`}
                  onChange={(e) => setFormValue(e.target.value, "riskCenter")}
                  margin={"none"}
                >
                  {
                    riskCenters && riskCenters.map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
          </>
        }
      </Grid>
      <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}> {/* tipo de feriado */}
        <FormControl style={{ width: "100%" }}>
          <InputLabel required id={`label-holidayType-${data ? data.internalCode : "new"}`}>{getLocalizedString("holidayType")}</InputLabel>
          <Select
            value={formData.holidayType}
            labelId={`label-holidayType-${data ? data.internalCode : "new"}`}
            id={`select-holidayType-${data ? data.internalCode : "new"}`}
            onChange={(e) => setFormValue(e.target.value, "holidayType")}
            margin={"none"}
          >
            {
              holidayTypes && holidayTypes.map(item => (
                <MenuItem
                  className={"whiteText"}
                  key={item.key}
                  value={item.key}>
                  {item.value}
                </MenuItem>
              ))
            }
          </Select>
        </FormControl>
      </Grid>
      {
        mapIsOpen &&
        <Grid container item alignItems="center" xs={12} style={{ paddingTop: 20, paddingBottom: 30 }}>
          <MapWithASearchBox
            open={mapIsOpen}
            handleSetData={handleSetMapData}
          />
        </Grid>
      }
      <Grid container item xs={12} justify={"flex-end"} alignItems="center" style={{ paddingBottom: 20 }}>
        <ButtonDialogAction isAccept onClick={submit} text={getLocalizedString("save")} />
      </Grid>
    </Grid>
  );
}

export default Form;